import React, { useMemo, createContext } from 'react';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';

import IvrLogsEditor from './IvrLogsEditor';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import ivrSessionsResource from 'src/actions/ivrs/ivrSessionsResource';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import useResource, { isLoadingStatus, getConsolidatedState } from 'src/presentation/hooks/useResource';
import { CloseButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { LinkToIvr } from './ivrLogsLayout';
import { transformDateToString } from './ivrLogsUtils';

export interface IvrLogsContextValue {
  ivrs: any[];
}

export const IvrLogsContext = createContext<IvrLogsContextValue>({
  ivrs: [],
});

const useIvrSession = () => {
  const { errors, loadingState: ivrSessionsLoadingState, values: ivrSessions } = useResource(ivrSessionsResource);
  const { loadingState: ivrsLoadingState, values: ivrConfigurations } = useResource(ivrsResource);

  const ivrSessionsWithIvrName = useMemo(() => {
    const res = map(ivrSessions, session => {
      const ivr = find(ivrConfigurations, ivr => get(ivr, 'id') === get(session, 'ivrId'));
      return { ...session, ivrName: get(ivr, 'name', get(ivr, 'description', get(session, 'ivrId'))) };
    });
    return sortBy(res, 'startTime').reverse();
  }, [ivrSessions, ivrConfigurations]);

  return {
    errors,
    loadingState: getConsolidatedState([ivrSessionsLoadingState, ivrsLoadingState]),
    ivrSessions: ivrSessionsWithIvrName,
    ivrConfigurations,
  };
};

const linkToIvrRender = row =>
  LinkToIvr({
    name: get(row, 'ivrName'),
    id: get(row, 'ivrId'),
    version: get(row, 'ivrVersion'),
  });

const columnConfig = [
  {
    attr: 'startTime',
    label: 'Time',
    customRenderer: item => transformDateToString(item.startTime),
    customColumnStyle: () => ({ width: '170px' }),
  },
  { attr: 'id', label: 'Session ID' },
  { attr: 'type', label: 'Type' },
  { attr: 'endpointAddress', label: 'Company Address' },
  { attr: 'customerAddress', label: 'Customer Address' },
  {
    attr: 'ivrName',
    label: 'IVR',
    customRenderer: linkToIvrRender,
  },
];

const IvrLogsConfiguration: TablePageRender = ({ root }) => {
  const { errors, loadingState, ivrSessions, ivrConfigurations } = useIvrSession();

  function filterFunc(items, searchString) {
    if (!searchString) {
      return items;
    }
    const results = filter(items, item => {
      const search = searchString.toLowerCase();

      const endpointAddress = get(item, 'endpointAddress').toLowerCase();
      const customerAddress = get(item, 'customerAddress').toLowerCase();
      const ivrName = get(item, 'ivrName').toLowerCase();
      const sessionId = get(item, 'id').toLowerCase();

      return (
        endpointAddress.includes(search) ||
        customerAddress.includes(search) ||
        sessionId.includes(search) ||
        ivrName.includes(search)
      );
    });
    return results;
  }

  const contextValue = {
    ivrs: ivrConfigurations,
  };

  return (
    <IvrLogsContext.Provider value={contextValue}>
      <TablePage
        columnConfig={columnConfig}
        data-aid="admin-ivr-logs-page"
        description=""
        editor={IvrLogsEditor}
        errors={errors}
        filterFunc={filterFunc}
        isLoading={isLoadingStatus(loadingState)}
        items={ivrSessions}
        modalCloseButtonLabel="Sessions List"
        modalCloseButtonType={CloseButtonTypes.BACK_BUTTON}
        name="Logging &amp; Troubleshooting"
        resetErrors={ivrSessionsResource.actions.resetErrors}
        root={root}
        skipItemExistenceCheck={true}
      />
    </IvrLogsContext.Provider>
  );
};

export default IvrLogsConfiguration;
